import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {InboxModel} from '../../../../../../../libs/shared/core/src/models/api/inboxModel';
import {DraftMessageModel} from '../../../../../../../libs/shared/core/src/models/api/messageModel';
import {TeammateModel} from '../../../../../../../libs/shared/core/src/models/api/teammateModel';
import {ReplyLocationsEnum} from '../../../../../../../libs/shared/core/src/services/tracking/tracking';
import {
  ComposerPanelId,
  ViewerPanelId,
} from '../../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {UserThemeConsumer} from '../../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {InboxesFormatter} from '../../../../../common/formatters/inboxesFormatter';
import {TeammatesFormatter} from '../../../../../common/formatters/teammatesFormatter';
import {ShareMessage} from '../../../../composer/share/shareButtonCoordinator';
import {ShareButtonCoordinatorContainer} from '../../../../composer/share/shareButtonCoordinatorContainer';
import {MessageBanner} from './messageBanner';
import {MessageBannerAction} from './messageBannerAction';
import {MessageBannerActionGroup, MessageBannerActionGroupProps} from './messageBannerActionGroup';
import {MessageBannerSubtitle} from './messageBannerSubtitle';
import {MessageBannerTitle} from './messageBannerTitle';
import {PrivateReplyTooltipCoordinatorContainer} from './privateReplyTooltipCoordinatorContainer';

/*
 * Props.
 */

interface DraftMessageBannerProps extends MessageBannerActionGroupProps {
  shareMessage: ShareMessage;
  message: DraftMessageModel;
  teamInboxes: ReadonlyArray<InboxModel>;
  participants: ReadonlyArray<TeammateModel>;
  canReplyToMessage: boolean;
  panelId: ComposerPanelId | ViewerPanelId;
  userId: number;
  isRounded: boolean;
  isComposerPopup: boolean;
}

/*
 * Style.
 */

const StyledTitleDiv = styled.div`
  display: flex;
  align-items: center;
`;

/*
 * Component.
 */

export const DraftMessageBanner: FC<DraftMessageBannerProps> = (props) => {
  const theme = useUserTheme();
  const visibilityText = maybeRenderVisibilityText(props);

  const shouldDisplayTooltip = props.canReplyToMessage && computeIsVisibleToOthers(props);

  return (
    <MessageBanner
      color={theme.alphas.gray20}
      iconName="draftFilledSmall"
      isRounded={props.isRounded}
      iconColor={theme.greys.shade80}
    >
      <MessageBannerTitle color={theme.greys.shade80}>
        <StyledTitleDiv>
          <Trans id="draftbanner-personal-title">Personal draft</Trans>
        </StyledTitleDiv>
      </MessageBannerTitle>
      <MessageBannerSubtitle color={theme.greys.shade80}>{visibilityText}</MessageBannerSubtitle>
      {shouldDisplayTooltip && (
        <PrivateReplyTooltipCoordinatorContainer
          location={ReplyLocationsEnum.DRAFT_BANNER_PRIVATE_REPLY_TOOLTIP}
          message={props.message}
          panelId={props.panelId}
          isComposerPopup={props.isComposerPopup}
        />
      )}
      <MessageBannerActionGroup
        onForcePopout={props.onForcePopout}
        onRequestClose={props.onRequestClose}
        color={theme.greys.shade80}
        useDarkHover
      >
        {renderShareButton(props)}
      </MessageBannerActionGroup>
    </MessageBanner>
  );
};

function renderShareButton(props: DraftMessageBannerProps) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <ShareButtonCoordinatorContainer
          shareMessage={props.shareMessage}
          message={props.message}
          renderButton={(isShared, _participantIds, isModalOpen, isDisabled) => (
            <MessageBannerActionGroup>
              <MessageBannerAction
                isDisabled={isShared || isDisabled}
                hasHover={isModalOpen}
                textColor={theme.interactiveText}
                useDarkHover
              >
                <Trans id="draftbanner-share">Share Draft</Trans>
              </MessageBannerAction>
            </MessageBannerActionGroup>
          )}
        />
      )}
    </UserThemeConsumer>
  );
}

/**
 * If the conversation is in a team inbox or has multiple participants, this clarifies such.
 * Otherwise, null is returned.
 */
function maybeRenderVisibilityText(props: DraftMessageBannerProps) {
  const {teamInboxes, participants, userId} = props;

  if (teamInboxes.length > 0) {
    const formattedInboxes = (
      <InboxesFormatter inboxes={teamInboxes} limit={2} renderInboxName={(name) => <strong>{name}</strong>} />
    );
    return (
      <Trans id="draftbanner-visibility-inboxes">
        This message will be visible to teammates in {formattedInboxes} after sending
      </Trans>
    );
  }

  if (participants.length > 1) {
    const formattedTeammates = (
      <TeammatesFormatter
        teammates={participants}
        userId={userId}
        renderTeammateName={(name) => <strong>{name}</strong>}
        limit={3}
      />
    );
    return (
      <Trans id="draftbanner-visibility-teammates">
        This message will be visible to {formattedTeammates} after sending
      </Trans>
    );
  }

  return <Trans id="autoshareddrafts-visibletoyou">Only visible to you</Trans>;
}

export function computeIsVisibleToOthers(props: DraftMessageBannerProps): boolean {
  return props.teamInboxes.length > 0 || props.participants.length > 1;
}
